import React, { useCallback, useMemo } from 'react'
import { ProgressReportByTeamPageConfig } from '../../../../../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { PageValidationError } from '../../../../../../../../../domain/entity/version-report/VersionReportTemplateEntity/validator'
import { ProjectPlanTemplateSelect } from '../../../../ProjectPlanSelect'
import { TeamTemplateSelectGrid } from '../../../../TeamSelect'
import { usePageConfigHandlers } from '../hooks/usePageConfigHandlers'
import {
  ConfigContainer,
  SectionContent,
  FormSection,
  FormLabel,
  PageTitleInput,
  ProjectPlanSelectWrapper,
  TeamSelectWrapper,
  ErrorBanner,
  ErrorMessages,
  ErrorMessage,
} from '../styles'
import { VersionReportTemplatePageFormModel } from '../../../model/VersionReportTemplatePageFormModel'

type ConfigFormProps = {
  page: VersionReportTemplatePageFormModel
  projectUuid: string
  validationErrors?: PageValidationError[]
  onUpdate: (
    pageNumber: number,
    updater: (
      prev: VersionReportTemplatePageFormModel
    ) => VersionReportTemplatePageFormModel
  ) => void
}

export const ProgressReportByTeamConfigForm: React.FC<ConfigFormProps> = ({
  page,
  projectUuid,
  validationErrors,
  onUpdate,
}) => {
  const config = useMemo(
    () => page.config as ProgressReportByTeamPageConfig,
    [page.config]
  )

  const { handlePageTitleChange } = usePageConfigHandlers(page, onUpdate)

  const handleRootUuidChange = useCallback(
    (uuid: string, selected: boolean) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByTeamPageConfig

        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            rootUuid: selected ? uuid : undefined,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  const handleTeamUuidChange = useCallback(
    (uuid: string, selected: boolean) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByTeamPageConfig
        const currentRows = currentConfig.tableRows || []

        const updatedTableRows = selected
          ? currentRows.includes(uuid)
            ? currentRows
            : [...currentRows, uuid]
          : currentRows.filter(id => id !== uuid)

        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            tableRows: updatedTableRows,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  return (
    <ConfigContainer>
      {validationErrors && validationErrors.length > 0 && (
        <ErrorBanner>
          <ErrorMessages>
            {validationErrors.map((error, index) => (
              <ErrorMessage key={index}>• {error.message}</ErrorMessage>
            ))}
          </ErrorMessages>
        </ErrorBanner>
      )}
      <SectionContent>
        <FormSection>
          <PageTitleInput
            label="ページタイトル"
            value={page.pageTitle}
            onChange={handlePageTitleChange}
            placeholder="ページのタイトルを入力してください"
            variant="outlined"
            required={true}
          />
        </FormSection>
        <FormSection>
          <FormLabel>集計基準となる工程を選択してください</FormLabel>
          <ProjectPlanSelectWrapper>
            <ProjectPlanTemplateSelect
              key={page.pageNumber}
              projectUuid={projectUuid}
              selectedUuids={config.rootUuid ? [config.rootUuid] : []}
              updateSelectedUuid={handleRootUuidChange}
            />
          </ProjectPlanSelectWrapper>
        </FormSection>

        <FormSection>
          <FormLabel>集計対象のチームを選択してください</FormLabel>
          <TeamSelectWrapper>
            <TeamTemplateSelectGrid
              key={page.pageNumber}
              projectUuid={projectUuid}
              selectedUuids={config.tableRows}
              updateSelectedUuid={handleTeamUuidChange}
            />
          </TeamSelectWrapper>
        </FormSection>
      </SectionContent>
    </ConfigContainer>
  )
}
